/**
 * One order, everything about it, and the four operations an academy admin may take on it.
 *
 * WHY THIS SCREEN EXISTS. The refund backend was complete, proven, and had moved real money in
 * Stripe — and an academy admin could not reach any of it, because there was no UI. The whole
 * path had only ever been driven by somebody signed in to a browser calling the API by hand,
 * which passes as a mechanism and fails as a product. This is the screen that closes that.
 *
 * ONE REQUEST FOR THE WHOLE SCREEN. `GET /api/payment-ops/v1/orders/<ref>` returns money,
 * access, the revocation plan, certificates, the audit timeline, obligations, the payment-method
 * truth and the controls block together. The pathways app already treats request fan-out as a
 * defect worth fixing — "41 requests became 1" is the standard recorded in this tree — and a
 * refund screen that assembled itself from six calls would also be a screen that could render
 * half a truth.
 *
 * THE FOUR OPERATIONS ARE DELIBERATELY SEPARATE and this screen keeps them separate:
 *
 *     Refund                       money moves; access is removed unless you untick
 *     Refund, keep access          money moves; nothing is revoked (a distinct audited action)
 *     Remove access, no refund     nothing reaches Stripe at all
 *     Restore access               the only reversible one
 *
 * Collapsing any two of them into one button with a checkbox is how an operator refunds when
 * they meant to revoke. The checkbox inside the refund dialog is the ONE exception, and it
 * exists because the server treats the two outcomes as two different audited actions.
 *
 * CONTROLS ARE ABSENT, NOT DISABLED, WHEN THE SERVER DID NOT SEND THEM. `writeGate` reads the
 * `controls` block: when the caller may not write, the `operations` key is not in the response
 * at all, so there is no affordance to re-enable in dev tools. That is the one place this
 * screen departs from the house rule of "visible and disabled with the reason beneath" — and
 * the reason is still stated, at the top, in the server's own sentence. Everything else that
 * cannot be done right now (a closed refund window, an amount below Stripe's minimum) stays
 * visible and disabled, because those are facts about the order rather than about the reader.
 *
 * IT SETTLES ITSELF WHILE A STATE IS IN FLIGHT, AND ONLY THEN
 * ----------------------------------------------------------
 * Measured: a learner pays, Stripe confirms about three seconds later, and this screen went on
 * showing `pending` until somebody reloaded it. An operator watching a payment come in had no
 * way to tell "not confirmed yet" from "this page is old", so the only way to find out what
 * happened was to reload and hope.
 *
 * THE COMMENT THAT USED TO BE ON THIS QUERY ARGUED AGAINST POLLING, and it was right about the
 * thing it was worried about: "polling a Stripe-backed screen forever costs an academy's rate
 * limit for a state change that may be six weeks away". An FPX refund really can take six
 * weeks, and a page that asked about it every five seconds for six weeks would be indefensible.
 *
 * So this is not that. Three properties, and each one answers that objection:
 *
 *   * IT ONLY RUNS FOR STATES THAT MOVE ON THEIR OWN IN SECONDS. `SETTLING_STATES` is an
 *     ALLOWLIST of two — `pending` and `refund_pending` — so `disputed`, `refunded` and every
 *     state a newer backend introduces refresh NEVER. This estate's own lesson from #6461:
 *     four states were introduced, two were branched on, and a benign one was read as a fault
 *     twice. A denylist would have polled the six-week case by default.
 *   * IT BACKS OFF AND IT STOPS. `nextRefreshMs` doubles 3s → 6s → 12s → 24s and gives up two
 *     minutes in: SEVEN requests, total, for the whole visit. Not seven per second, and not
 *     seven per item — this screen is one request for the whole order (see above), so there is
 *     no fan-out to multiply. `openedx_thinlms_pathways/.../completion.py` records what
 *     per-item fan-out did to this estate; a fixed-interval poll here would be a smaller
 *     version of the same mistake.
 *   * IT PAUSES WHEN THE TAB IS NOT VISIBLE, because react-query's default is
 *     `refetchIntervalInBackground: false`. A forgotten tab costs nothing.
 *
 * And when it gives up it SAYS SO rather than going quiet, because a silent stop is
 * indistinguishable from the stale screen this exists to fix.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../AdminShell';
import {
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  EmptyState,
  Spinner,
  Toolbar,
} from '../../shared/ui';
import type { Column } from '../../shared/ui';
import { formatMoney } from '../../shared/lib/pricing';
import { GrantPlan } from '../payments/GrantPlan';
import { RefundDialog } from '../payments/RefundDialog';
import { CancelAccessDialog } from '../payments/CancelAccessDialog';
import { RegrantAccessDialog } from '../payments/RegrantAccessDialog';
import { ObligationDialog } from '../payments/ObligationDialog';
import {
  OPS_COPY,
  PAYMENT_OPS_KEY,
  auditKey,
  explainOpsFailure,
  loadOrderScreen,
  may,
  orderDetailKey,
  refundBasis,
  windowState,
  writeGate,
  type WireAuditEntry,
  type WireObligation,
  type WireOrderScreen,
} from '../api/paymentOps';
import '../payments/payments.css';

type OpenDialog = 'refund' | 'cancel' | 'regrant' | { obligation: WireObligation } | null;

const OUTCOME_TONE: Record<string, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  requested: 'info',
  succeeded: 'success',
  partially_succeeded: 'warning',
  failed: 'danger',
};

/**
 * The ONLY payment states this screen refreshes itself for. AN ALLOWLIST, deliberately.
 *
 * `Order.PaymentState` has twelve members and more will be added. Two of them move on their
 * own within seconds of a learner or an operator acting: `pending` becomes `paid` when Stripe's
 * webhook lands — measured at about three seconds — and `refund_pending` becomes `refunded`
 * when a card refund confirms.
 *
 * Everything else is either settled or slow. `disputed` sits with a bank for weeks;
 * `refund_pending` on an FPX charge can too, which is why the refresh below is bounded by time
 * as well as by state — it covers the fast case and gives up rather than following the slow one.
 *
 * A DENYLIST HERE WOULD BE THE BUG. A state added by a newer backend would fall into
 * "refresh it", and the first such state to be a long-lived one would have this screen polling
 * an academy's Stripe-backed endpoint for weeks. Not refreshing an unknown state costs an
 * operator one manual reload, which is the direction to fail in.
 */
export const SETTLING_STATES = new Set(['pending', 'refund_pending']);

/** First delay, the cap it doubles up to, and when to give up. See `nextRefreshMs`. */
const REFRESH_FIRST_MS = 3_000;
const REFRESH_MAX_MS = 24_000;
const REFRESH_CEILING_MS = 2 * 60_000;

/**
 * How long to wait before the next read, given how long this screen has been open.
 *
 * EXPORTED FOR ITS TEST, and only for that. A backoff written inline inside a
 * `refetchInterval` closure is a backoff nobody can check, and the two properties worth
 * checking are exactly the two that answer the objection to polling at all: that the delays
 * GROW, and that it STOPS.
 *
 * The schedule is 3s, 6s, 12s, 24s, 24s… from a two-minute ceiling, which is seven reads for
 * the whole visit. Derived from elapsed time rather than from an attempt counter so that it is
 * a pure function of one number — a counter would need a ref, and a ref that survives a
 * remount is a poll that never backs off.
 *
 * `false` is react-query's own "stop", so giving up needs no separate flag.
 */
export function nextRefreshMs(elapsedMs: number): number | false {
  let delay = REFRESH_FIRST_MS;
  let scheduledAt = delay;
  // THE CEILING IS ALSO THE LOOP'S BOUND, and it has to be. Walking only on
  // `scheduledAt <= elapsedMs` never terminates for an `elapsedMs` of `Infinity`: the delay
  // caps at 24s, so `scheduledAt` climbs for ever without ever exceeding it. Nothing can
  // currently pass `Infinity` — the caller subtracts two `Date.now()` readings — but an
  // exported pure function that hangs on one input is a hang waiting for its first caller.
  while (scheduledAt <= elapsedMs && scheduledAt <= REFRESH_CEILING_MS) {
    delay = Math.min(delay * 2, REFRESH_MAX_MS);
    scheduledAt += delay;
  }
  // THE CEILING IS CHECKED AGAINST WHEN THE READ WOULD HAPPEN, not against now. Checking
  // `elapsedMs` instead lets the last read be scheduled at 117s and fire at 141s — one request
  // past the limit the docstring promises, which is the sort of off-by-one that makes a stated
  // bound untrue.
  return scheduledAt <= REFRESH_CEILING_MS ? delay : false;
}

/**
 * A settled fact, an unknown, and an emphatic none are three different renders.
 *
 * `amount_minor` is `null` on the operations that move no money, and `no_money_moved: true` is
 * the affirmative marker for that. An em dash there would read as "we do not know what this
 * cost", which is a different and much worse statement than "this cost nothing".
 */
function auditAmount(entry: WireAuditEntry) {
  if (entry.no_money_moved) return <span className="admin-muted">No money moved</span>;
  if (entry.amount_minor === null || entry.amount_minor === undefined) {
    return <span className="admin-muted">{OPS_COPY.unknownCount}</span>;
  }
  return formatMoney(entry.amount_minor, entry.currency || 'MYR');
}

function auditColumns(): Column<WireAuditEntry>[] {
  return [
    {
      key: 'when',
      header: 'When',
      render: (row) => new Date(row.occurred_at).toLocaleString(),
    },
    { key: 'action', header: 'Action', render: (row) => row.action },
    {
      key: 'outcome',
      header: 'Outcome',
      render: (row) => (
        <>
          <Badge tone={OUTCOME_TONE[row.outcome] ?? 'neutral'}>{row.outcome}</Badge>
          {/* `requested` is not done. An FPX refund can sit here for six weeks, and a screen
              that shows it as finished is the reason somebody tells a learner it is finished. */}
          {row.outcome === 'requested' && !row.settled_at ? (
            <>
              <br />
              <small className="admin-muted">not yet confirmed by Stripe</small>
            </>
          ) : null}
          {row.outcome_detail ? (
            <>
              <br />
              <small className="admin-muted">{row.outcome_detail}</small>
            </>
          ) : null}
        </>
      ),
    },
    {
      key: 'actor',
      header: 'Who',
      render: (row) => (
        <>
          {row.actor_username || <span className="admin-muted">unattributed</span>}
          {/* A superadmin acting for an academy under a recorded mandate is a materially
              different row from an academy admin acting for themselves, and the audit trail
              is the only place that distinction survives. */}
          {row.acted_under_mandate ? (
            <>
              {' '}
              <Badge tone="warning">under mandate</Badge>
            </>
          ) : null}
        </>
      ),
    },
    { key: 'amount', header: 'Amount', numeric: true, render: auditAmount },
    {
      key: 'reason',
      header: 'Reason given',
      render: (row) =>
        row.reason || <span className="admin-muted">— (recorded by the system, not a person)</span>,
    },
  ];
}

export function AdminOrderDetail() {
  const { orderRef = '' } = useParams();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const [done, setDone] = useState<string | null>(null);
  // WHEN THIS VISIT STARTED, fixed for the life of the mount. The backoff is a function of it,
  // so a re-render must not move it — which is what `useState` with an initialiser gives and a
  // bare `Date.now()` in the body would not.
  const [openedAt] = useState(() => Date.now());

  const query = useQuery({
    queryKey: orderDetailKey(orderRef),
    queryFn: () => loadOrderScreen(orderRef),
    enabled: orderRef !== '',
    // A refund is requested and confirmed asynchronously, so this screen is stale the moment a
    // webhook lands.
    staleTime: 10_000,
    retry: false,
    // The bounded, backing-off, allowlisted refresh. See the module docstring for why this is
    // not the "poll a Stripe-backed screen forever" that the previous comment here refused.
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      if (!SETTLING_STATES.has(data.order.payment_state)) return false;
      return nextRefreshMs(Date.now() - openedAt);
    },
  });

  /**
   * Everything on this screen reads under one root key, so one invalidation reconciles the
   * order, the list behind it and the audit trail after a write. Same reason `PRICING_SEAM_KEY`
   * exists: three surfaces disagreeing about whether a refund happened is worse than a reload.
   */
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: PAYMENT_OPS_KEY });
    void queryClient.invalidateQueries({ queryKey: auditKey(orderRef) });
  };

  if (!orderRef) {
    return <Banner tone="danger">No order was named in this address.</Banner>;
  }
  if (query.isPending) return <Spinner label="Loading this order…" />;

  if (query.isError || !query.data) {
    const failure = explainOpsFailure(query.error);
    return (
      <>
        <PageHeader title="Order" />
        <Banner tone="danger">
          {failure.message}
          {failure.nextStep ? (
            <>
              <br />
              {failure.nextStep}
            </>
          ) : null}
        </Banner>
        <p>
          <Link to="/admin/payments">Back to payments</Link>
        </p>
      </>
    );
  }

  const screen: WireOrderScreen = query.data;
  const gate = writeGate(screen.controls);
  const basis = refundBasis(screen);
  const window = windowState(screen.truth_banner);
  const currency = screen.refund.currency || screen.order.charge_currency || 'MYR';
  const settling = SETTLING_STATES.has(screen.order.payment_state);
  const givenUp = settling && nextRefreshMs(Date.now() - openedAt) === false;

  return (
    <>
      <PageHeader
        title={`Order ${screen.order.order_ref}`}
        description={`${screen.order.learner.username} · ${screen.order.item.kind} · ${screen.order.item.key}`}
        actions={<Link to="/admin/payments">Back to payments</Link>}
      />

      {/* The read-only reason, in the server's own words, at the top — because the operator
          needs to know now, not after hunting for a button that is not there. Three different
          refusals with three different next actions; the sentence names which. */}
      {gate.kind !== 'writable' ? <Banner tone="info">{gate.reason}</Banner> : null}

      {/* WHAT THE SCREEN IS DOING, said out loud. An operator who cannot tell "waiting for
          Stripe" from "this page is old" reloads, which is the behaviour being fixed — so the
          refresh has to be visible, and so does its end. `role="status"` because the sentence
          changes underneath a reader who is not looking at it. */}
      {settling ? (
        <Banner tone="info">
          <span role="status" aria-live="polite">
            {givenUp
              ? `This order is still ${screen.order.payment_state}. This screen has stopped ` +
                'checking after two minutes — reload to ask again.'
              : `This order is ${screen.order.payment_state}. This screen is checking for you ` +
                'and will update itself; there is no need to reload.'}
          </span>
        </Banner>
      ) : null}

      {done ? <Banner tone="success">{done}</Banner> : null}

      <div className="ops-detail">
        <main>
          <Card title="Money">
            <div className="ops-money">
              <div className="ops-money__row">
                <span>Payment state</span>
                <b>
                  <Badge tone={screen.order.payment_state === 'paid' ? 'success' : 'neutral'}>
                    {screen.order.payment_state}
                  </Badge>
                </b>
              </div>
              <div className="ops-money__row">
                <span>Access state</span>
                <b>
                  <Badge
                    tone={screen.order.access_state === 'granted' ? 'success' : 'warning'}
                  >
                    {screen.order.access_state}
                  </Badge>
                </b>
              </div>
              {basis.state === 'settled' ? (
                <>
                  <div className="ops-money__row">
                    <span>
                      Charged
                      <small className="admin-muted">
                        course price {formatMoney(basis.listMinor, currency)} + payment processing{' '}
                        {formatMoney(basis.processingMinor, currency)}
                        {basis.discountMinor > 0
                          ? `, less ${formatMoney(basis.discountMinor, currency)} of promotion applied at checkout`
                          : ''}
                      </small>
                    </span>
                    <b>{formatMoney(basis.chargedMinor, currency)}</b>
                  </div>
                  <div className="ops-money__row ops-money__row--total">
                    <span>Still refundable</span>
                    <b>{formatMoney(basis.refundableMinor, currency)}</b>
                  </div>
                </>
              ) : (
                /* NULL IS NOT ZERO. An order where no payment ever settled has no charge to
                   show, and rendering RM 0.00 here would read as a free purchase. */
                <div className="ops-money__row">
                  <span>Charged</span>
                  <b className="admin-muted">Nothing ever settled on this order</b>
                </div>
              )}
            </div>
            <p className="admin-muted">{OPS_COPY.moneyIsTheAcademys}</p>
          </Card>

          <Card title="What a refund would do to access">
            <GrantPlan
              decisions={screen.grant_decisions}
              accessState={screen.order.access_state}
            />
          </Card>

          <Card title="Audit trail for this order">
            {screen.audit.length === 0 ? (
              <EmptyState
                title="Nothing has been done to this order yet"
                body="Every refund, access change and settlement writes a permanent row here, with the reason the operator gave."
              />
            ) : (
              <DataTable
                columns={auditColumns()}
                rows={screen.audit}
                rowKey={(row) => `${row.operation_id}:${row.action}`}
                empty={null}
              />
            )}
          </Card>
        </main>

        <aside>
          <Card title="Operations">
            {gate.kind === 'writable' ? (
              <Toolbar>
                {/* Visible and disabled with the reason beneath, because a closed window is a
                    fact about the order rather than about the reader. Submitting anyway and
                    letting Stripe refuse would manufacture a failure we could already see. */}
                {may(gate, 'refund') ? (
                  <Button
                    variant="danger"
                    disabled={!screen.refund_available || window.kind === 'closed'}
                    onClick={() => setDialog('refund')}
                  >
                    Refund
                  </Button>
                ) : null}
                {may(gate, 'cancel_access') ? (
                  <Button variant="secondary" onClick={() => setDialog('cancel')}>
                    Remove access without refunding
                  </Button>
                ) : null}
                {may(gate, 'regrant_access') ? (
                  <Button variant="secondary" onClick={() => setDialog('regrant')}>
                    Restore access
                  </Button>
                ) : null}
              </Toolbar>
            ) : (
              <p className="admin-muted">{gate.reason}</p>
            )}

            {gate.kind === 'writable' && !screen.refund_available ? (
              <Banner tone="warning">
                {screen.refund_unavailable_reason ||
                  'Stripe will not accept a refund on this payment.'}
              </Banner>
            ) : null}
            <p className="admin-muted">{OPS_COPY.auditIsPermanent}</p>
          </Card>

          <Card title="This payment method">
            <div className="ops-truth" data-tone={screen.truth_banner.tone}>
              <h4>
                {screen.truth_banner.label}
                {screen.truth_banner.disputable ? null : (
                  <Badge tone="info">Cannot be disputed</Badge>
                )}
              </h4>
              {screen.truth_banner.lines.map((line) => (
                <p key={line}>{line}</p>
              ))}
              {/* Three window states, and they say different things. An unbounded one has no
                  deadline at all, and printing "0 days left" for a card payment would be a
                  fabrication. `urgent` is absent from the payload when unbounded. */}
              {window.kind === 'unbounded' ? (
                <p className="admin-muted">No refund deadline on this method.</p>
              ) : null}
              {window.kind === 'open' ? (
                <p className={window.urgent ? undefined : 'admin-muted'}>
                  Refundable through Stripe until {window.deadline.slice(0, 10)}
                  {window.daysLeft === null ? '' : ` — ${window.daysLeft} days left`}.
                </p>
              ) : null}
              {window.kind === 'closed' ? (
                <p>
                  The refund window closed
                  {window.deadline ? ` on ${window.deadline.slice(0, 10)}` : ''}. Stripe will not
                  accept a refund on this payment, so the learner has to be paid another way and
                  recorded here.
                </p>
              ) : null}
            </div>
          </Card>

          <Obligations
            screen={screen}
            writable={gate.kind === 'writable'}
            onSettle={(obligation) => setDialog({ obligation })}
          />
        </aside>
      </div>

      {dialog === 'refund' ? (
        <RefundDialog
          screen={screen}
          onClose={() => setDialog(null)}
          onDone={(result) => {
            setDialog(null);
            // NEVER "refunded". The server answered `requested`, asynchronously confirmed.
            setDone(
              `${formatMoney(result.amount_minor, result.currency)} refund requested. ${OPS_COPY.requested}` +
                (result.balance.state === 'unknown' ? ` ${OPS_COPY.balanceUnknown}` : ''),
            );
            refresh();
          }}
        />
      ) : null}

      {dialog === 'cancel' ? (
        <CancelAccessDialog
          screen={screen}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            setDone('Access removed. No money moved and Stripe was not contacted.');
            refresh();
          }}
        />
      ) : null}

      {dialog === 'regrant' ? (
        <RegrantAccessDialog
          screen={screen}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            setDone(
              'Access restored. Any certificate that was revoked stays revoked — restoring access does not undo that.',
            );
            refresh();
          }}
        />
      ) : null}

      {dialog && typeof dialog === 'object' ? (
        <ObligationDialog
          obligation={dialog.obligation}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            setDone('Obligation updated.');
            refresh();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Money this academy owes a learner because a refund FAILED at Stripe.
 *
 * FPX refunds can fail outright, and when one does the learner's access has already been
 * removed while their money has not come back. An obligation is the row that stops that being
 * forgotten, so it is on the order screen rather than filed somewhere an operator would have
 * to remember to look.
 */
function Obligations({
  screen,
  writable,
  onSettle,
}: {
  screen: WireOrderScreen;
  writable: boolean;
  onSettle: (obligation: WireObligation) => void;
}) {
  if (screen.obligations.length === 0) return null;

  return (
    <Card title="Owed to this learner">
      {screen.obligations.map((obligation) => (
        <div key={obligation.id} className="ops-obligation">
          <p>
            <b>{formatMoney(obligation.amount_minor, obligation.currency)}</b>{' '}
            <Badge tone={obligation.state === 'open' ? 'danger' : 'neutral'}>
              {obligation.state}
            </Badge>
          </p>
          <p className="admin-muted">
            {obligation.failure_reason
              ? `Stripe could not send this refund: ${obligation.failure_reason}.`
              : 'A refund on this order did not reach the learner.'}{' '}
            Opened {new Date(obligation.opened_at).toLocaleDateString()}.
          </p>
          <p className="admin-muted">
            {/* An empty assignee is UNASSIGNED, and it is the deliberate default: naming the
                platform here would imply the platform will settle it, and it cannot. */}
            {obligation.assignee
              ? `Assigned to ${obligation.assignee}.`
              : 'Nobody is assigned to this yet.'}
          </p>
          {writable && obligation.state === 'open' ? (
            <Button variant="secondary" onClick={() => onSettle(obligation)}>
              Record how this was settled
            </Button>
          ) : null}
        </div>
      ))}
    </Card>
  );
}
